import React from 'react';
import { Building2, Hospital, Sparkles, Box } from 'lucide-react';

const ServicesSection = () => {
  const services = [
    {
      icon: Building2,
      title: 'Construction',
      description: 'Accurate 3D reality capture for complex construction projects and live environments.',
      color: 'from-blue-500 to-cyan-500',
      image: 'https://images.unsplash.com/photo-1599995903128-531fc7fb694b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHxjb25zdHJ1Y3Rpb24lMjBzaXRlfGVufDB8fHx8MTc3MjY5NDkyNnww&ixlib=rb-4.1.0&q=85&w=800',
      alt: '3D laser scanning and point cloud capture on a construction site',
    },
    {
      icon: Hospital,
      title: 'Healthcare',
      description: 'Supporting patients and families through their healthcare journey. Virtual tours help reduce anxiety and improve preparedness for clinical environments.',
      color: 'from-green-500 to-emerald-500',
      image: 'https://images.unsplash.com/photo-1516841273335-e39b37888115?w=800&q=85',
      alt: 'NHS hospital environment for 3D virtual tour scanning',
    },
    {
      icon: Building2,
      title: 'Leisure',
      description: 'Comprehensive 3D scanning solutions for hotels, spas, resorts and hospitality venues.',
      color: 'from-yellow-500 to-orange-500',
      image: 'https://images.unsplash.com/photo-1532926381893-7542290edf1d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBzcGF8ZW58MHx8fHwxNzcyNzE5NDI1fDA&ixlib=rb-4.1.0&q=85&w=800',
      alt: 'Luxury spa and hospitality venue for Matterport 3D virtual tours',
    },
    {
      icon: Sparkles,
      title: 'Hologram Technology',
      description: 'Cutting-edge immersive visualization for stakeholder engagement and presentations.',
      color: 'from-purple-500 to-pink-500',
      image: 'https://images.unsplash.com/photo-1771599940657-f9f151abed45?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwzfHxob2xvZ3JhbSUyMGRpZ2l0YWwlMjB2aXN1YWxpemF0aW9ufGVufDB8fHx8MTc3MjcxNzc5M3ww&ixlib=rb-4.1.0&q=85',
      alt: 'Hologram digital twin visualisation for stakeholder presentations',
    },
    {
      icon: Box,
      title: 'CGI Staging',
      description: 'Photorealistic CGI and visualization for commercial fit-out and heritage projects.',
      color: 'from-red-500 to-pink-500',
      image: 'https://images.unsplash.com/photo-1601002257790-ebe0966a85ae?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjB2aWxsYSUyMGludGVyaW9yfGVufDB8fHx8MTc3MjcxOTQyOHww&ixlib=rb-4.1.0&q=85&w=800',
      alt: 'Photorealistic CGI visualisation of a luxury villa interior by Digital Scanning Group',
    },
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-full mb-6">
            <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
            <span className="text-blue-600 text-sm font-semibold">OUR SERVICES</span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
            Comprehensive Digital Solutions
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            We deliver accurate, visual, and defensible digital records using advanced 3D scanning and immersive technologies.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <div
                key={index}
                className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
              >
                {/* Image Background */}
                <div className="relative h-64 overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent z-10"></div>
                  <img
                    src={service.image}
                    alt={service.alt || service.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  {/* Icon */}
                  <div className="absolute top-6 left-6 z-20">
                    <div className={`p-4 bg-gradient-to-br ${service.color} rounded-xl shadow-xl`}>
                      <Icon className="h-8 w-8 text-white" />
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">{service.description}</p>
                  <button className="mt-6 text-blue-600 font-semibold flex items-center gap-2 group-hover:gap-4 transition-all">
                    Learn More
                    <span className="text-xl">→</span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;